const applyAccessibilityFallbacks = (root = document) => {
  root.querySelectorAll('video > img:not([alt])').forEach((image) => {
    image.setAttribute('alt', '');
  });
};

const normalizeProductDescriptionHeadings = (root = document) => {
  root.querySelectorAll('#key-features > h4').forEach((heading) => {
    const replacement = document.createElement('h2');

    for (const attribute of heading.attributes) {
      replacement.setAttribute(attribute.name, attribute.value);
    }

    replacement.append(...heading.childNodes);
    heading.replaceWith(replacement);
  });
};

const makeScrollableRegionsKeyboardAccessible = (root = document) => {
  const regions = [];

  if (root instanceof Element && root.matches('.rte-table-wrapper')) {
    regions.push(root);
  }

  regions.push(...root.querySelectorAll('.rte-table-wrapper'));

  for (const region of regions) {
    if (!region.hasAttribute('tabindex')) region.setAttribute('tabindex', '0');
  }
};

const normalizeRebuyCartHeadings = (root = document) => {
  const cart = root instanceof Element
    ? root.closest('#rebuy-cart') || root.querySelector('#rebuy-cart')
    : root.querySelector('#rebuy-cart');
  if (!cart) return;

  cart.querySelectorAll('h4, h5, [role="heading"][aria-level="4"], [role="heading"][aria-level="5"]').forEach((heading) => {
    heading.setAttribute('role', 'heading');
    heading.setAttribute('aria-level', '3');
  });

  cart.querySelectorAll('.rebuy-product-block[aria-label]:not([role])').forEach((slide) => {
    slide.setAttribute('role', 'group');
  });
};

const syncRebuyCartInertState = (root = document) => {
  const carts = [];

  if (root instanceof Element && root.matches('#rebuy-cart')) {
    carts.push(root);
  }

  carts.push(...root.querySelectorAll('#rebuy-cart'));

  for (const cart of carts) {
    cart.toggleAttribute('inert', cart.getAttribute('aria-hidden') !== 'false');
  }
};

const initializeAutoplayVideoControls = (root = document) => {
  const videos = [];

  if (root instanceof HTMLVideoElement) {
    videos.push(root);
  }

  videos.push(...root.querySelectorAll('video[autoplay][loop]:not([controls])'));

  for (const video of videos) {
    if (video.dataset.accessibleVideoControl === 'true') continue;

    video.dataset.accessibleVideoControl = 'true';

    const container = video.parentElement;
    if (!container) continue;

    container.classList.add('accessible-video-control-container');

    const button = document.createElement('button');
    const icon = document.createElement('span');
    const context = container.closest('.shopify-section')?.querySelector('h1, h2, h3')?.textContent.trim();
    button.type = 'button';
    button.className = 'accessible-video-toggle';
    icon.setAttribute('aria-hidden', 'true');
    button.append(icon);
    video.insertAdjacentElement('afterend', button);

    const updateControl = () => {
      const isPaused = video.paused;
      const label = isPaused ? document.body.dataset.playVideoLabel : document.body.dataset.pauseVideoLabel;
      button.setAttribute(
        'aria-label',
        context ? `${label}: ${context}` : label
      );
      icon.textContent = isPaused ? '▶' : '❚❚';
    };

    button.addEventListener('click', () => {
      if (video.paused) {
        video.dataset.motionUserStarted = 'true';
        video.play();
      } else {
        video.pause();
      }
    });

    video.addEventListener('play', () => {
      if (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
        video.dataset.motionUserStarted !== 'true'
      ) {
        video.pause();
      }
      updateControl();
    });
    video.addEventListener('pause', updateControl);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      video.pause();
    }

    updateControl();
  }
};

const applyReducedMotionPreference = () => {
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.querySelectorAll('video[autoplay][loop]:not([controls])').forEach((video) => {
    if (video.dataset.motionUserStarted !== 'true') video.pause();
  });
};

document.addEventListener('rebuy:smartcart.show', () => {
  document.querySelector('#rebuy-cart')?.removeAttribute('inert');
});

document.addEventListener('rebuy:smartcart.hide', () => {
  document.querySelector('#rebuy-cart')?.setAttribute('inert', '');
});

document.addEventListener('DOMContentLoaded', () => {
  applyAccessibilityFallbacks();
  normalizeProductDescriptionHeadings();
  makeScrollableRegionsKeyboardAccessible();
  normalizeRebuyCartHeadings();
  syncRebuyCartInertState();
  initializeAutoplayVideoControls();
  applyReducedMotionPreference();

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) {
          if (node.matches('video > img:not([alt])')) {
            node.setAttribute('alt', '');
          }
          applyAccessibilityFallbacks(node);
          normalizeProductDescriptionHeadings(node);
          makeScrollableRegionsKeyboardAccessible(node);
          normalizeRebuyCartHeadings(node);
          syncRebuyCartInertState(node);
          initializeAutoplayVideoControls(node);
          applyReducedMotionPreference();
        }
      }

      if (mutation.type === 'attributes' && mutation.target instanceof Element) {
        syncRebuyCartInertState(mutation.target);
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden'],
  });

  window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', applyReducedMotionPreference);
});
